import { ApiClient } from 'lib/api/ApiClient.types';
import {
  featureFlagSetToWireEntries,
  parseFeatureFlagsResponse,
  toFeatureFlagSet
} from 'store/teams/featureFlags.bridge';
import { resolveWebV1ApiRoot } from 'v5/api/enterprise/enterprise.api.http.utils';
import { z } from 'zod';
import {
  EnterpriseFeatureFlagApi,
  FeatureFlagSet
} from './enterprise-feature-flag.api';

const flagsByTeam$ = z.object({
  data: z.array(
    z.object({
      dbSchema: z.string(),
      featureFlags: z.unknown()
    })
  )
});

function featureFlagSetToPutBody(set: FeatureFlagSet) {
  return {
    feature_flags: featureFlagSetToWireEntries(set)
  };
}

export function createEnterpriseFeatureFlagApi(
  client: ApiClient
): EnterpriseFeatureFlagApi {
  return {
    // Flags ride along with the team list — there is no dedicated GET. Absolute
    // /web/v1/auth URLs bypass ApiClient isV2 / ported-v1-endpoints (same as
    // getAccountTeams) so workspace flag rows are never dropped.
    getFlagSet: async organizationId => {
      const response = await client.get(
        `${resolveWebV1ApiRoot()}/auth/account/teams`,
        { token: true }
      );
      const { data: teams } = flagsByTeam$.parse(response);
      const team = teams.find(item => item.dbSchema === organizationId);
      if (!team) {
        throw new Error(
          `No team found for organization ${organizationId} while reading feature flags`
        );
      }
      return toFeatureFlagSet(parseFeatureFlagsResponse(team.featureFlags));
    },

    saveFlagSet: async (organizationId, set) => {
      await client.put(
        `${resolveWebV1ApiRoot()}/auth/tenant/teams/${organizationId}/feature-flags`,
        {
          token: true,
          data: featureFlagSetToPutBody(set)
        }
      );
    }
  };
}
